import { useState, useCallback, useMemo } from 'react';

const KEY = 'melodiza:history';
const MAX = 500;
const HALF_LIFE_MS = 2 * 60 * 60 * 1000; // 2h

export function useHistory() {
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
  });

  const log = useCallback((trackId) => {
    setHistory(prev => {
      const next = [{ trackId, playedAt: Date.now() }, ...prev.filter(e => e.trackId !== trackId)].slice(0, MAX);
      localStorage.setItem(KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(KEY);
  }, []);

  // Exponential decay weight (half-life 2h): returns 1.0 at t=0, 0.5 at t=2h, etc.
  const getDecayWeight = useCallback((trackId) => {
    const entry = history.find(h => h.trackId === trackId);
    if (!entry) return 0;
    const age = Date.now() - (entry.playedAt || 0);
    return Math.pow(0.5, age / HALF_LIFE_MS);
  }, [history]);

  // List of recently-played tracks with decay weight > 0.1 (i.e. played within ~6.6h)
  const recentlyDecayed = useMemo(() => {
    const now = Date.now();
    return history
      .map(h => ({
        trackId: h.trackId,
        weight: Math.pow(0.5, (now - h.playedAt) / HALF_LIFE_MS),
        playedAt: h.playedAt,
      }))
      .filter(x => x.weight > 0.1);
  }, [history]);

  return { history, log, clear, getDecayWeight, recentlyDecayed };
}
